import {
  createContext,
  Fragment,
  useCallback,
  useContext,
  useLayoutEffect,
  useMemo,
  useState,
  type ReactNode,
} from 'react';

import { loadCatalog } from './load.ts';
import { dirOf, langOf, type Locale, type ShippedLocale } from './locale.ts';
import { rememberLocale } from './negotiate.ts';
import { setLocale, t } from './t.ts';

type LocaleContextValue = {
  locale: Locale;
  /**
   * Switch to a locale once its catalog has loaded; `false` if it could not
   * be, and the page stays as it was. A no-op under `qps`, which is a
   * developer's view and outranks any learner's choice.
   */
  choose: (locale: ShippedLocale) => Promise<boolean>;
};

const LocaleContext = createContext<LocaleContextValue>({ locale: 'en', choose: async () => false });

export function useLocale(): LocaleContextValue {
  return useContext(LocaleContext);
}

/**
 * Owns the interface locale: sets it before anything below renders, and puts
 * it on `<html lang dir>` and the tab title (spec §4.3, §4.5).
 *
 * Children are keyed on the locale, so a switch remounts everything that
 * called `t()` rather than leaving stale copy in memoised subtrees. The route
 * guards' network-only `me` query runs again on that remount.
 */
export function LocaleProvider({ initial, children }: { initial: Locale; children: ReactNode }) {
  const [locale, setState] = useState<Locale>(initial);

  const choose = useCallback(async (next: ShippedLocale) => {
    const loaded = await loadCatalog(next);
    if (loaded) {
      rememberLocale(next);
      setState((current) => (current === 'qps' ? current : next));
    }
    return loaded;
  }, []);

  // During render, not in an effect: the children's `t()` calls read it in
  // this same render pass. Idempotent, so StrictMode's double render is safe.
  setLocale(locale);
  // A layout effect, so an Arabic page never paints one frame left to right.
  useLayoutEffect(() => {
    document.documentElement.lang = langOf(locale);
    document.documentElement.dir = dirOf(locale);
    document.title = t('app-title');
  }, [locale]);

  const value = useMemo(() => ({ locale, choose }), [locale, choose]);
  return (
    <LocaleContext.Provider value={value}>
      <Fragment key={locale}>{children}</Fragment>
    </LocaleContext.Provider>
  );
}
